import { useSortableTable } from '../../../hooks/useSortableTable';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../../utils/currency';
import { navigateWithPreference } from '../../../utils/navigationHelper';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';

function Quotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  
  // Sorting
  const { items: sortedQuotes, requestSort, getSortIndicator } = useSortableTable(quotes, { key: 'quote_number', direction: 'desc' });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    quoteRef: true,
    customer: true,
    site: true,
    quoteDate: true,
    validUntil: true,
    total: true,
    profit: true,
    status: true,
  });

  useEffect(() => {
    fetchQuotes();
    loadColumnPreferences();
  }, []);

  const loadColumnPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/quotes');
      const data = await response.json();
      if (data.preferences) {
        setVisibleColumns(data.preferences);
      }
    } catch (error) {
      console.error('Error loading column preferences:', error);
    }
  };

  const saveColumnPreferences = async (newPreferences) => {
    try {
      await apiFetch('/api/preferences/default/quotes', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: newPreferences })
      });
    } catch (error) {
      console.error('Error saving column preferences:', error);
    }
  };

  const toggleColumn = (column) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [column]: !visibleColumns[column]
    };
    setVisibleColumns(newVisibleColumns);
    saveColumnPreferences(newVisibleColumns);
  };

  const allColumns = [
    { key: 'quoteRef', label: 'Quote Ref' },
    { key: 'customer', label: 'Customer' },
    { key: 'site', label: 'Site' },
    { key: 'quoteDate', label: 'Quote Date' },
    { key: 'validUntil', label: 'Valid Until' },
    { key: 'total', label: 'Total' },
    { key: 'profit', label: 'Profit' },
    { key: 'status', label: 'Status' },
  ];

  const fetchQuotes = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await apiFetch('/api/quotes');
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Quote list failed with status ${response.status}`);
      }
      const data = await response.json();
      setQuotes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      const message = error.response?.data?.error || error.message || 'Could not load quotes';
      setLoadError(message);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredQuotes = sortedQuotes.filter(quote =>
    quote.quote_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      expired: 'bg-orange-100 text-orange-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      sent: 'Sent',
      accepted: 'Accepted',
      rejected: 'Rejected',
      expired: 'Expired',
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Quotes"
        subtitle={`${quotes.length} total`}
        backButton
        onBack={() => navigate('/ops')}
        backTitle="Back to Operations"
        sticky
        actionButton={
          <button
            onClick={() => navigate('/ops/quotes/new')}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ New Quote
          </button>
        }
      />

      <div className="px-4 py-3">
        {/* Search + Column Selector */}
        <div className="mb-3 flex items-center gap-2">
          <input
            type="text"
            placeholder="Search quotes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
          />
          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition flex items-center gap-2"
            >
              ⚙️ Columns
            </button>
            {showColumnSelector && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowColumnSelector(false)}
                />
                <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-2">
                  <div className="px-3 py-2 border-b border-gray-200">
                    <div className="text-xs font-semibold text-gray-600">Show/Hide Columns</div>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {allColumns.map(col => (
                      <label
                        key={col.key}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[col.key]}
                          onChange={() => toggleColumn(col.key)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{col.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded border border-gray-200 ">
          <div className="overflow-auto max-h-[calc(100vh-240px)] relative"><table className="w-full text-sm">
            <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
              <tr>
                {visibleColumns.quoteRef && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('quote_ref')}>Quote Ref{getSortIndicator('quote_ref')}</th>}
                {visibleColumns.customer && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('customer_name')}>Customer{getSortIndicator('customer_name')}</th>}
                {visibleColumns.site && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('site_name')}>Site{getSortIndicator('site_name')}</th>}
                {visibleColumns.quoteDate && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('quote_date')}>Date{getSortIndicator('quote_date')}</th>}
                {visibleColumns.validUntil && <th className="text-left px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('valid_until')}>Valid Until{getSortIndicator('valid_until')}</th>}
                {visibleColumns.total && <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('total')}>Total{getSortIndicator('total')}</th>}
                {visibleColumns.profit && <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('total_profit')}>Profit{getSortIndicator('total_profit')}</th>}
                {visibleColumns.status && <th className="text-center px-3 py-1.5 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('status')}>Status{getSortIndicator('status')}</th>}
                <th className="text-right px-3 py-1.5 text-xs font-semibold text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-3 py-6 text-center text-gray-500 text-sm">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2"></div>
                    Loading...
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td colSpan="9" className="px-3 py-6 text-center text-sm">
                    <div className="max-w-xl mx-auto bg-red-50 border border-red-200 rounded-lg p-4 text-left">
                      <div className="font-semibold text-red-900 mb-1">Quotes could not load</div>
                      <div className="text-red-800 mb-3">{loadError}</div>
                      <button
                        onClick={fetchQuotes}
                        className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition"
                      >
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : filteredQuotes.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-3 py-6 text-center text-gray-500 text-sm">
                    {searchTerm ? 'No matches.' : 'No quotes yet.'}
                  </td>
                </tr>
              ) : (
                filteredQuotes.map((quote, index) => (
                  <tr
                    key={quote.id}
                    onClick={() => navigateWithPreference(navigate, `/ops/quotes/${quote.id}`, 'quotes')}
                    className={`hover:bg-blue-200 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-100' : 'bg-white'}`}
                  >
                    {visibleColumns.quoteRef && (
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{quote.quote_ref}</div>
                      </td>
                    )}
                    {visibleColumns.customer && (
                      <td className="px-3 py-2 text-gray-700">{quote.customer_name}</td>
                    )}
                    {visibleColumns.site && (
                      <td className="px-3 py-2 text-gray-700">{quote.site_name || 'Head Office'}</td>
                    )}
                    {visibleColumns.quoteDate && (
                      <td className="px-3 py-2 text-gray-700">
                        {quote.quote_date ? new Date(quote.quote_date).toLocaleDateString() : '-'}
                      </td>
                    )}
                    {visibleColumns.validUntil && (
                      <td className="px-3 py-2 text-gray-700">
                        {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : '-'}
                      </td>
                    )}
                    {visibleColumns.total && (
                      <td className="px-3 py-2 text-right font-medium text-gray-900">
                        {formatCurrency(quote.total)}
                      </td>
                    )}
                    {visibleColumns.profit && (
                      <td className="px-3 py-2 text-right">
                        <span className={`font-medium ${parseFloat(quote.total_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(quote.total_profit)}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">
                          ({parseFloat(quote.profit_margin || 0).toFixed(1)}%)
                        </span>
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded ${getStatusColor(quote.status)}`}>
                          {getStatusLabel(quote.status)}
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/ops/quotes/${quote.id}`);
                        }}
                        className="text-purple-600 hover:text-purple-800 text-xs font-medium mr-3"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-600">
          Showing {filteredQuotes.length} of {quotes.length}
        </div>
      </div>
    </div>
  );
}

export default Quotes;
